import { FC, useEffect } from "react";
import clsx from "clsx";
import {
  useTonAddress,
  useTonConnectUI,
  useTonWallet,
} from "@tonconnect/ui-react";
import { useAccount } from "wagmi";
import { useAuth } from "../../hooks/useAuth";
import { useTonWalletAssociation } from "../../hooks/useTonWalletAssociation";
import type { WalletAssociationState } from "../../hooks/useWalletAssociation";
import { TON_CONNECT_TEXT } from "../../constants/walletAssociation.constants";
import Button from "../button/button";
import { shortenTonAddress } from "./ton-connect-button.utils";
import styles from "./TonConnectButton.module.scss";

interface TonConnectButtonProps {
  onAssociated?: () => void;
  onError?: (error: string) => void;
  disabled?: boolean;
  className?: string;
  associationState?: Pick<
    WalletAssociationState,
    "isAssociated" | "isLoading" | "isCreating" | "error"
  >;
}

export const TonConnectButton: FC<TonConnectButtonProps> = ({
  onAssociated,
  onError,
  disabled = false,
  className,
  associationState,
}) => {
  const [tonConnectUI] = useTonConnectUI();
  const tonAddress = useTonAddress();
  const wallet = useTonWallet();
  const isAuth = useAuth();
  const { address: evmAddress } = useAccount();

  const localAssociationState = useTonWalletAssociation({
    requiresTon: !associationState,
  });
  const { isAssociated, isLoading, isCreating, error } =
    associationState ?? localAssociationState;

  useEffect(() => {
    if (isAssociated) {
      onAssociated?.();
    }
  }, [isAssociated, onAssociated]);

  useEffect(() => {
    if (error) {
      onError?.(error);
    }
  }, [error, onError]);

  if (!isAuth || !evmAddress) {
    return (
      <div
        className={clsx({
          [styles.tonConnectButton]: true,
          ...(className ? { [className]: true } : {}),
        })}
      >
        <p className={styles.requirementText}>
          {TON_CONNECT_TEXT.CONNECT_EVM_FIRST}
        </p>
      </div>
    );
  }

  const handleConnect = () => {
    void tonConnectUI.openModal();
  };

  const handleDisconnect = () => {
    void tonConnectUI.disconnect();
  };

  const renderBody = () => {
    if (!wallet) {
      return (
        <Button
          variant="outlined"
          className={styles.connectButton}
          disabled={disabled}
          onClick={handleConnect}
        >
          {TON_CONNECT_TEXT.CONNECT_WALLET}
        </Button>
      );
    }

    if (isAssociated) {
      return (
        <div className={styles.connectedState}>
          <div className={styles.walletInfo}>
            <span className={styles.successIcon}>✓</span>
            <span className={styles.walletAddress}>
              {shortenTonAddress(tonAddress)}
            </span>
          </div>
          <Button
            variant="outlined"
            className={styles.disconnectButton}
            onClick={handleDisconnect}
          >
            {TON_CONNECT_TEXT.DISCONNECT}
          </Button>
        </div>
      );
    }

    return (
      <div className={styles.associationState}>
        <div className={styles.walletInfo}>
          <span className={styles.walletAddress}>
            {shortenTonAddress(tonAddress)}
          </span>
        </div>
        <div className={styles.buttonGroup}>
          {isLoading || isCreating ? (
            <p className={styles.loadingText}>{TON_CONNECT_TEXT.ASSOCIATING}</p>
          ) : (
            <>
              <p className={styles.statusText}>
                {TON_CONNECT_TEXT.ASSOCIATION_IN_PROGRESS}
              </p>
              <Button
                variant="outlined"
                className={styles.disconnectButton}
                onClick={handleDisconnect}
              >
                {TON_CONNECT_TEXT.DISCONNECT}
              </Button>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div
      className={clsx({
        [styles.tonConnectButton]: true,
        ...(className ? { [className]: true } : {}),
      })}
    >
      {renderBody()}
      {error && <p className={styles.errorText}>{error}</p>}
    </div>
  );
};

export default TonConnectButton;
